import axios, { AxiosResponse, AxiosError } from 'axios';

// Type definitions
export interface Cocktail {
  cocktail_id: number;
  name: string;
  family?: string;
  iba_status?: string;
  method?: string;
  glass?: string;
  instructions?: string;
  created_at?: string;
  updated_at?: string;
  family_name?: string;
  method_name?: string;
  glassware_name?: string;
  difficulty?: number;
  prep_time_seconds?: number;
  rating_avg?: number;
  rating_count?: number;
  strength_band?: string;
  abv_serve?: number;
  visual_notes?: any;
  tasting_notes?: any;
  ingredients?: Array<{
    ingredient_id: number;
    name: string;
    quantity: number;
    unit: string;
    prep_note?: string;
    optional: boolean;
  }>;
}

export interface CocktailFilters {
  search?: string;
  family?: string;
  method?: string;
  glassware?: string;
  difficulty_max?: number;
  prep_time_max?: number;
  strength_band?: string;
  base_spirit?: string;
  flavor_tags?: string[];
  occasion_tags?: string[];
  dietary_flags?: string[];
  limit?: number;
  offset?: number;
}

export interface CocktailStats {
  total_cocktails: number;
  families: number;
  methods: number;
  glasses: number;
  avg_abv: number | null;
  avg_difficulty: number | null;
  avg_prep_time: number | null;
  low_strength_count: number;
  standard_strength_count: number;
  strong_strength_count: number;
}

// Configuration interface
interface RumDBConfig {
  baseURL: string;
  tunnelUrl: string | null;
  timeout: number;
}

// Global configuration
declare global {
  interface Window {
    RumDBConfig?: RumDBConfig;
    TunnelDiscovery?: {
      getCurrentUrl: () => string | null;
      forceUpdate: () => void;
    };
  }
}

// Default configuration
const defaultConfig: RumDBConfig = {
  baseURL: 'http://localhost:3001',
  tunnelUrl: null,
  timeout: 10000
};

// Initialize configuration
let config: RumDBConfig = { ...defaultConfig };

// Function to discover and update tunnel URL
async function discoverTunnelUrl(): Promise<string | null> {
  // Check for manually set tunnel URL first
  if (typeof window !== 'undefined') {
    const manualUrl = localStorage.getItem('rumdb_manual_tunnel_url');
    if (manualUrl) {
      console.log('🔧 Found manual tunnel URL:', manualUrl);
      try {
        const testResponse = await axios.get(`${manualUrl}/health`, {
          timeout: 5000
        });
        if (testResponse.status === 200) {
          console.log('✅ Manual tunnel URL is working');
          return manualUrl;
        } else {
          console.log('⚠️ Manual tunnel URL returned non-200 status:', testResponse.status);
          localStorage.removeItem('rumdb_manual_tunnel_url');
        }
      } catch (testError: any) {
        console.log('⚠️ Manual tunnel URL test failed:', testError?.message || 'Unknown error');
        localStorage.removeItem('rumdb_manual_tunnel_url');
      }
    }
  }

  // For deployed environment, ALWAYS use the known tunnel URL (never fall back to localhost)
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    const deployedTunnelUrl = 'https://api.rumdb.com';
    console.log('🌐 Deployed environment detected, using:', deployedTunnelUrl);
    
    // For deployed environments, we don't test the URL - we just use it
    // The CORS test might fail due to browser security, but the actual API calls will work
    return deployedTunnelUrl;
  }

  // For local development, try to get from backend
  try {
    const response = await axios.get('http://localhost:3001/tunnel/url', {
      timeout: 5000
    });
    if (response.data && response.data.tunnelUrl) {
      const url = response.data.tunnelUrl;
      console.log('🔍 Testing tunnel URL from backend:', url);
      // Test if this URL works
      try {
        const testResponse = await axios.get(`${url}/health`, {
          timeout: 5000
        });
        if (testResponse.status === 200) {
          console.log('✅ Tunnel URL from backend is working, using tunnel');
          return url.trim();
        } else {
          console.log('⚠️ Tunnel URL from backend returned non-200 status:', testResponse.status);
        }
      } catch (testError: any) {
        console.log('⚠️ Tunnel URL from backend test failed:', testError?.message || 'Unknown error');
      }
    }
  } catch (error: any) {
    console.log('⚠️ Could not get tunnel URL from backend:', error?.message || 'Unknown error');
  }
  
  // Only fall back to localhost for local development
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('🔄 No working tunnel URL found, using localhost');
    return null;
  }
  
  // For deployed environments, never fall back to localhost
  console.log('🌐 Deployed environment - no fallback to localhost allowed');
  return 'https://api.rumdb.com';
}

// Function to update base URL based on tunnel URL
function updateBaseURL(tunnelUrl: string | null) {
  if (tunnelUrl) {
    config.baseURL = tunnelUrl;
    config.tunnelUrl = tunnelUrl;
    console.log('🌐 Updated API base URL to:', config.baseURL);
  } else {
    config.baseURL = defaultConfig.baseURL;
    config.tunnelUrl = null;
    console.log('🌐 Using local API base URL:', config.baseURL);
  }
  
  // Update global config
  if (typeof window !== 'undefined') {
    window.RumDBConfig = config;
  }
  
  // Update axios default base URL
  axios.defaults.baseURL = config.baseURL;
  
  // Update apiClient base URL
  apiClient.defaults.baseURL = config.baseURL;
}

// Initialize tunnel discovery
async function initializeTunnelDiscovery() {
  console.log('🔍 Initializing tunnel discovery...');
  
  // Try to discover current tunnel URL
  const tunnelUrl = await discoverTunnelUrl();
  updateBaseURL(tunnelUrl);
  
  // Set up periodic discovery
  setInterval(async () => {
    const newTunnelUrl = await discoverTunnelUrl();
    if (newTunnelUrl !== config.tunnelUrl) {
      console.log('🌐 Tunnel URL changed, updating...');
      updateBaseURL(newTunnelUrl);
    }
  }, 30000); // Check every 30 seconds
  
  // Listen for tunnel URL updates from the discovery script
  if (typeof window !== 'undefined') {
    window.addEventListener('tunnelUrlUpdated', ((event: CustomEvent) => {
      const { url } = event.detail;
      if (url !== config.tunnelUrl) {
        console.log('🌐 Tunnel URL updated via event:', url);
        updateBaseURL(url);
      }
    }) as EventListener);
  }
}

// Manual tunnel URL update function
export function updateTunnelUrlManually(newUrl: string) {
  console.log('🔧 Manual tunnel URL update:', newUrl);
  
  // Test the new URL first
  axios.get(`${newUrl}/health`, { timeout: 5000 })
    .then(() => {
      console.log('✅ Manual tunnel URL is working');
      updateBaseURL(newUrl);
      
      // Update global config for persistence
      if (typeof window !== 'undefined') {
        window.RumDBConfig = {
          ...config,
          tunnelUrl: newUrl,
          baseURL: newUrl
        };
        
        // Store in localStorage for persistence
        localStorage.setItem('rumdb_manual_tunnel_url', newUrl);
      }
    })
    .catch((error) => {
      console.error('❌ Manual tunnel URL test failed:', error.message);
      alert(`Tunnel URL test failed: ${error.message}`);
    });
}

// Function to get current tunnel URL
export function getCurrentTunnelUrl(): string | null {
  return config.tunnelUrl;
}

// Function to reset to default
export function resetTunnelUrl() {
  console.log('🔄 Resetting tunnel URL to default');
  updateBaseURL(null);
  
  if (typeof window !== 'undefined') {
    localStorage.removeItem('rumdb_manual_tunnel_url');
  }
}

// Initialize on module load
initializeTunnelDiscovery();

// Expose functions globally for debugging
if (typeof window !== 'undefined') {
  (window as any).RumDBDebug = {
    updateTunnelUrl: updateTunnelUrlManually,
    getCurrentTunnelUrl,
    resetTunnelUrl,
    getConfig: () => config
  };
  
  console.log('🔧 RumDB Debug tools available:');
  console.log('  - RumDBDebug.updateTunnelUrl("https://your-tunnel-url.com")');
  console.log('  - RumDBDebug.getCurrentTunnelUrl()');
  console.log('  - RumDBDebug.resetTunnelUrl()');
  console.log('  - RumDBDebug.getConfig()');
}

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: config.baseURL,
  timeout: config.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add tunnel URL discovery
apiClient.interceptors.request.use(async (config) => {
  // If we don't have a tunnel URL, try to discover one
  if (!config.baseURL?.includes('trycloudflare.com') && !config.baseURL?.includes('localhost')) {
    const tunnelUrl = await discoverTunnelUrl();
    if (tunnelUrl) {
      config.baseURL = tunnelUrl;
      updateBaseURL(tunnelUrl);
    }
  }
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('⏰ Request timeout - server may be offline');
      
      // Try to discover new tunnel URL on timeout
      try {
        const tunnelUrl = await discoverTunnelUrl();
        if (tunnelUrl && tunnelUrl !== config.tunnelUrl) {
          console.log('🔄 Updating tunnel URL due to timeout...');
          updateBaseURL(tunnelUrl);
        }
      } catch (discoveryError) {
        console.error('❌ Failed to discover tunnel URL:', discoveryError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Error handling wrapper
function addErrorHandling<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((error: AxiosError) => {
    console.error('🔍 API Error Details:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        timeout: error.config?.timeout
      }
    });
    
    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
      throw new Error(`API Error: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
    } else if (error.request) {
      console.error('Network Error:', error.message);
      throw new Error('API Error: Network Error');
    } else {
      console.error('Request Error:', error.message);
      throw new Error(`API Error: ${error.message}`);
    }
  });
}

// API functions
export const ApiService = {
  // Health check
  async getHealth(): Promise<any> {
    return addErrorHandling(apiClient.get('/health'));
  },

  // Database stats
  async getDatabaseStats(): Promise<any> {
    console.log('🔍 Calling getDatabaseStats with baseURL:', config.baseURL);
    try {
      const result = await addErrorHandling(apiClient.get('/stats'));
      console.log('✅ getDatabaseStats result:', result);
      return result.data; // Return the data, not the full response
    } catch (error) {
      console.error('❌ getDatabaseStats failed:', error);
      throw error;
    }
  },

  // Venues
  async getVenues(filters?: {
    limit?: number;
    offset?: number;
    venue_type?: string;
    city?: string;
    region?: string;
    search?: string;
    featured?: boolean;
    min_rating?: number;
    price_tier?: string;
    has_website?: boolean;
    has_phone?: boolean;
  }): Promise<any> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    
    console.log('🔍 Calling getVenues with baseURL:', config.baseURL, 'params:', params.toString());
    try {
      const result = await addErrorHandling(apiClient.get(`/venues?${params.toString()}`));
      console.log('✅ getVenues result length:', result?.data?.length || 'undefined');
      return result.data; // Return the data, not the full response
    } catch (error) {
      console.error('❌ getVenues failed:', error);
      throw error;
    }
  },

  async getVenue(id: number): Promise<any> {
    try {
      const result = await addErrorHandling(apiClient.get(`/venues/${id}`));
      return result.data; // Venue detail API returns object directly
    } catch (error) {
      console.error('❌ getVenue failed:', error);
      throw error;
    }
  },

  async getFeaturedVenues(): Promise<any> {
    return addErrorHandling(apiClient.get('/venues?limit=6'));
  },

  async getVenueStats(): Promise<any> {
    try {
      const result = await addErrorHandling(apiClient.get('/venues/stats/overview'));
      return result.data;
    } catch (error) {
      console.error('❌ getVenueStats failed:', error);
      throw error;
    }
  },

  async getVenueTypes(): Promise<string[]> {
    try {
      const result = await addErrorHandling(apiClient.get('/venues/types/list'));
      return result.data;
    } catch (error) {
      console.error('❌ getVenueTypes failed:', error);
      throw error;
    }
  },

  // Rums
  async getRums(limit?: number, offset?: number): Promise<any> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    try {
      const result = await addErrorHandling(apiClient.get(`/rums?${params.toString()}`));
      return result.data; // Return the data directly since API returns array
    } catch (error) {
      console.error('❌ getRums failed:', error);
      throw error;
    }
  },

  async getRum(id: number): Promise<any> {
    try {
      const result = await addErrorHandling(apiClient.get(`/rums/${id}`));
      return result.data; // Rum detail API returns object directly
    } catch (error) {
      console.error('❌ getRum failed:', error);
      throw error;
    }
  },

  async getFeaturedRums(): Promise<any> {
    return addErrorHandling(apiClient.get('/rums?limit=6'));
  },

  // Cocktails
  async getCocktails(filters?: CocktailFilters): Promise<Cocktail[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }
    
    console.log('🔍 Calling getCocktails with params:', params.toString());
    try {
      const result = await addErrorHandling(apiClient.get(`/cocktails?${params.toString()}`));
      console.log('✅ getCocktails result length:', result?.data?.length || 'undefined');
      return result.data;
    } catch (error) {
      console.error('❌ getCocktails failed:', error);
      throw error;
    }
  },

  async getCocktailStats(): Promise<CocktailStats> {
    console.log('🔍 Calling getCocktailStats');
    try {
      const result = await addErrorHandling(apiClient.get('/cocktails/stats'));
      console.log('✅ getCocktailStats result:', result);
      return result.data;
    } catch (error) {
      console.error('❌ getCocktailStats failed:', error);
      throw error;
    }
  },

  async getCocktail(id: number): Promise<Cocktail> {
    try {
      const result = await addErrorHandling(apiClient.get(`/cocktails/${id}`));
      return result.data; // Cocktail detail API returns object directly
    } catch (error) {
      console.error('❌ getCocktail failed:', error);
      throw error;
    }
  },

  async getFeaturedCocktails(): Promise<any> {
    return addErrorHandling(apiClient.get('/cocktails?limit=6'));
  },

  // Distilleries
  async getDistilleries(limit?: number, offset?: number): Promise<any> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    try {
      const result = await addErrorHandling(apiClient.get(`/distilleries?${params.toString()}`));
      return result.data; // Return the data directly since API returns array
    } catch (error) {
      console.error('❌ getDistilleries failed:', error);
      throw error;
    }
  },

  async getDistillery(id: number): Promise<any> {
    try {
      const result = await addErrorHandling(apiClient.get(`/distilleries/${id}`));
      return result.data; // Distillery detail API returns object directly
    } catch (error) {
      console.error('❌ getDistillery failed:', error);
      throw error;
    }
  },

  async getDistilleryById(id: number): Promise<any> {
    return this.getDistillery(id);
  },

  async getFeaturedDistilleries(): Promise<any> {
    return addErrorHandling(apiClient.get('/distilleries?limit=6'));
  },

  // Culture
  async getCultureArticles(): Promise<any> {
    return addErrorHandling(apiClient.get('/culture'));
  },

  async getCulturalContent(limit?: number, offset?: number): Promise<any> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    try {
      const result = await addErrorHandling(apiClient.get(`/culture?${params.toString()}`));
      return result.data; // Return the data directly since API returns array
    } catch (error) {
      console.error('❌ getCulturalContent failed:', error);
      throw error;
    }
  },

  async getCulturalContentStats(): Promise<any> {
    try {
      const result = await addErrorHandling(apiClient.get('/culture?limit=1'));
      return result.data; // Return the data directly since API returns array
    } catch (error) {
      console.error('❌ getCulturalContentStats failed:', error);
      throw error;
    }
  },

  // Blog
  async getBlogPosts(limit?: number, offset?: number): Promise<any> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    try {
      const result = await addErrorHandling(apiClient.get(`/blog?${params.toString()}`));
      return result.data.data; // Blog API returns {success, data, pagination}
    } catch (error) {
      console.error('❌ getBlogPosts failed:', error);
      throw error;
    }
  },

  async getBlogPost(id: number): Promise<any> {
    try {
      const result = await addErrorHandling(apiClient.get(`/blog/${id}`));
      return result.data.data; // Blog API returns {success, data}
    } catch (error) {
      console.error('❌ getBlogPost failed:', error);
      throw error;
    }
  },

  async getBlogCategories(): Promise<any> {
    try {
      const result = await addErrorHandling(apiClient.get('/blog/categories/list'));
      return result.data.data; // Blog API returns {success, data}
    } catch (error) {
      console.error('❌ getBlogCategories failed:', error);
      throw error;
    }
  },

  // Tunnel management
  async getTunnelUrl(): Promise<any> {
    return addErrorHandling(apiClient.get('/tunnel-url'));
  },

  async updateTunnelUrl(url: string): Promise<any> {
    return addErrorHandling(apiClient.post('/tunnel-url', { url }));
  },

  // Utility functions
  getCurrentConfig(): RumDBConfig {
    return { ...config };
  },

  async forceTunnelDiscovery(): Promise<void> {
    const tunnelUrl = await discoverTunnelUrl();
    updateBaseURL(tunnelUrl);
  },

  formatPrepTime(seconds: number): string {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) return `${minutes}m`;
    return `${minutes}m ${remainingSeconds}s`;
  }
};

export default ApiService; 